import { Component } from '@theme/component';

/**
 * Reveals its descendants once it first scrolls into view, by setting `revealed` on itself.
 *
 * Replaces the old theme's `[data-reveal]` observer. That one added `s--revealed`, fired a
 * `revealed` event and then unobserved, so the animation ran once per page load — the same
 * one-shot behaviour is kept here. Its `rootMargin` also matched the old theme: nothing on
 * phones, `-100px` above, so wider screens start the transition slightly later.
 *
 * Children opt in with `eq-reveal--up` / `eq-reveal--in`, and stagger with `eq-reveal--delay-*`.
 *
 * @extends {Component}
 */
class EqReveal extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    // Without the observer, or when motion is reduced, show everything rather than leaving the
    // section stuck at `opacity: 0`.
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#reveal();
      return;
    }

    const rootMargin = window.matchMedia('(max-width: 749px)').matches ? '0px' : '-100px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#reveal();
          this.#observer?.disconnect();
        }
      },
      { rootMargin }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #reveal() {
    this.toggleAttribute('revealed', true);
    this.dispatchEvent(new CustomEvent('revealed', { bubbles: true }));
  }
}

if (!customElements.get('eq-reveal')) {
  customElements.define('eq-reveal', EqReveal);
}
